import { useMemo } from "react"
import { type UseQueryExecute, useQuery } from "urql"

import {
  type AssociatedTestTemplate,
  type AssociatedTestTemplateListOutputFailure,
  type AssociatedTestTemplateListOutputSuccess,
  type AssociatedTestTemplateSortField,
  GetProductAssociatedTestTemplateListDocument,
  type SortingOrder,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseGetAssociatedTestTemplateListProps {
  productId: number
  field?: AssociatedTestTemplateSortField
  order?: SortingOrder
}

interface UseGetAssociatedTestTemplateListReturn {
  associatedTestTemplates: AssociatedTestTemplate[]
  isFetching: boolean
  refetch: UseQueryExecute
}

export const useGetAssociatedTestTemplateList = ({
  productId,
  field,
  order,
}: UseGetAssociatedTestTemplateListProps): UseGetAssociatedTestTemplateListReturn => {
  const [{ data, fetching, error }, reexecuteQuery] = useQuery({
    query: GetProductAssociatedTestTemplateListDocument,
    variables: {
      productId,
      ...(field && { field }),
      ...(order && { order }),
    },
  })
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)

  const associatedTestTemplates = useMemo(() => {
    if (!data) return []

    if (data.associatedTestTemplate.list.__typename === "AssociatedTestTemplateListOutputFailure" || error) {
      notify({
        message: "Failed to fetch product associated test templates",
        content:
          error?.message ?? (data.associatedTestTemplate.list as AssociatedTestTemplateListOutputFailure).error.message,
        type: NotificationType.Error,
      })
      return []
    }

    return (data.associatedTestTemplate.list as AssociatedTestTemplateListOutputSuccess)
      .associatedTestTemplates as AssociatedTestTemplate[]
  }, [data, notify, error])

  return {
    associatedTestTemplates,
    isFetching: fetching,
    refetch: reexecuteQuery,
  }
}
